// src/screens/payments/SponsorPaymentScreen.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './SponsorPaymentScreen.css';
import backIcon from '../../assets/backspace-icon.png';
import api from '../../api';

function SponsorPaymentScreen() {
  const navigate = useNavigate();
  const { id: productId } = useParams();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);

  // screen-only toast (success | error | info)
  const [toast, setToast] = useState({ visible: false, message: '', type: '' });
  const showToast = (message, type = 'success', opts = {}) => {
    const { onClose, duration = 2200 } = opts;
    setToast({ visible: true, message, type });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => {
      setToast({ visible: false, message: '', type: '' });
      if (typeof onClose === 'function') onClose();
    }, duration);
  };

  // focusable error + smooth scroll (accessibility)
  const errorRef = useRef(null);
  const showFocusError = (msg) => {
    showToast(msg, 'error', { duration: 2600 });
    setTimeout(() => {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        errorRef.current?.focus?.();
      } catch {}
    }, 0);
  };

  // 🌐 Mainnet gate (mirror of MerchantPaymentScreen)
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isTestnetSub = /^testnet\./i.test(host);
  const isBarterfiRoot = /(^|\.)barterfi\.app$/i.test(host);
  const isMainnet = isBarterfiRoot && !isTestnetSub;

  useEffect(() => {
    if (!productId) return;
    (async () => {
      try {
        const res = await api.get(`/products/${productId}`);
        const productData = res.data.product || res.data;
        setProduct(productData);
      } catch (err) {
        console.error('❌ Failed to load product:', err);
      }
    })();
  }, [productId]);

  const handleConfirmPayment = async () => {
    // 🔒 Block unless on Mainnet
    if (!isMainnet) {
      showFocusError(
        'Sponsorship payment is only available on Mainnet (barterfi.app). Open the mainnet URL to proceed.'
      );
      return;
    }

    setLoading(true);
    try {
      const res = await api.patch(
        `/sponsorships/mark-paid/${productId}`,
        {},
        { headers: { 'X-Network': 'mainnet' } }
      );

      const msg = (res.data?.message || '').toLowerCase();
      if (msg.includes('already sponsored')) {
        showToast(
          'ℹ️ This product is already sponsored. Please wait for the current promotion to expire.',
          'error',
          { onClose: () => navigate('/merchant-store') }
        );
      } else if (msg.includes('payment completed') || res.data?.success === true) {
        showToast(
          '✅ Payment successful! Your product is now promoted for 7 days.',
          'success',
          { onClose: () => navigate('/marketplace', { replace: true }) }
        );
      } else {
        showToast('⚠️ Something went wrong. Please try again.', 'error');
      }
    } catch (err) {
      console.error('❌ Sponsorship payment failed:', err);
      const data = err?.response?.data;
      let serverMsg =
        data?.message || data?.error || err?.message || 'Payment failed. Please try again later.';

      // friendly mappings
      if (data?.error === 'mainnet_only') {
        serverMsg = 'This payment must be completed on Mainnet (barterfi.app).';
      } else if (data?.code === 'WALLET_NOT_FOUND' || /no wallet found/i.test(serverMsg)) {
        serverMsg = 'No wallet found. Please activate your wallet first to complete payment.';
      } else if (data?.code === 'INSUFFICIENT_FUNDS' || /insufficient|not enough/i.test(serverMsg)) {
        serverMsg = 'You do not have enough Pi in your wallet to complete this payment.';
      }

      showFocusError(serverMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sponsor-payment-container">
      <div className="sponsor-payment-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="sponsor-back-icon"
          onClick={() => navigate(-1)}
        />
        <h2 className="sponsor-payment-title">Sponsorship Payment</h2>
      </div>

      <div className="sponsor-payment-body">
        {/* 🌐 Helpful banner if opened on testnet */}
        {!isMainnet && (
          <div
            className="sponsorpay-warning"
            role="alert"
            aria-live="polite"
            tabIndex={-1}
            ref={errorRef}
          >
            You’re on <strong>Testnet</strong>. Sponsorship payments require real Pi on
            <strong> Mainnet</strong>. Open <strong>barterfi.app</strong> to continue.
          </div>
        )}

        <div className="sponsor-payment-info">
          <p>
            Your product has been approved for promotion. To activate your sponsored listing,
            please confirm the payment of <strong>15 Pi</strong>.
          </p>
          <p>
            Sponsored products appear at the top of the <strong>Social feed</strong> for 7 days,
            helping you reach more buyers.
          </p>
        </div>

        {product ? (
          <div className="sponsor-preview-card">
            <img
              src={product.media?.[0] || require('../../assets/placeholder-image.png')}
              alt={product.name}
              className="sponsor-preview-image"
              onError={(e) => {
                e.target.src = require('../../assets/placeholder-image.png');
              }}
            />
            <div className="sponsor-preview-details">
              <div className="sponsor-preview-name">{product.name}</div>
              <div className="sponsor-preview-price">
                {product.pricingType === 'dynamic'
                  ? `${product.usdPrice} USD`
                  : `${product.price} Pi`}
              </div>
              <div className="sponsor-preview-label">(Sponsored Preview)</div>
            </div>
          </div>
        ) : (
          <p style={{ marginTop: '20px', color: '#888' }}>Loading product preview...</p>
        )}
      </div>

      <div className="sponsor-payment-footer">
        <button
          className="sponsor-payment-cancel"
          onClick={() => navigate(-1)}
          disabled={loading}
        >
          Cancel
        </button>

        <button
          className="sponsor-payment-confirm"
          onClick={handleConfirmPayment}
          disabled={loading || !isMainnet}
          title={!isMainnet ? 'Open barterfi.app (Mainnet) to pay' : ''}
        >
          {loading ? 'Processing…' : (!isMainnet ? 'Mainnet Only' : 'Confirm & Pay')}
        </button>
      </div>

      {toast.visible && (
        <div className={`sponsorpay-toast ${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default SponsorPaymentScreen;
